// Copyright (c) Ping. All rights reserved.

/**
 * Location represents a point on the map. The `lat` / `lng` numbers
 * live in this module in MEMORY ONLY and NEVER reach a logger
 * (Hard rule 3). They cross the wire to the in-chat card as an opaque
 * payload at the host's discretion — this module does not serialise
 * them outside the {@link ShareLocationResult} returned from the
 * modal's `onShare` callback.
 */
export interface Location {
  readonly lat: number;
  readonly lng: number;
  readonly label?: string;
  readonly address?: string;
}

export type LocationShareKind = 'point' | 'live';

export interface ShareLocationResult {
  readonly location: Location;
  readonly kind: LocationShareKind;
  /** Duration (seconds) for a live share; `undefined` for `point`. */
  readonly liveDurationSeconds?: number;
}

/**
 * The live-share duration choices the duration sub-modal offers, mapped to
 * seconds. Figma 3629:7114 — 15 minutes / 1 hour / 24 hours.
 */
export const LIVE_DURATION_OPTIONS = [
  { value: 15 * 60, label: '15 minutes' },
  { value: 60 * 60, label: '1 hour' },
  { value: 24 * 60 * 60, label: '24 hours' },
] as const;

export type LocationTab = 'static' | 'current';

export interface LocationSearchResult {
  /** Opaque provider-side id. Never logged. */
  readonly id: string;
  readonly label: string;
  readonly address?: string;
  readonly distanceMeters?: number;
  /**
   * Coordinates for the suggestion. Held in memory only; never logged.
   */
  readonly location: Location;
}

export interface ShareLocationCallbacks {
  readonly onClose: () => void;
  readonly onShare: (result: ShareLocationResult) => Promise<void> | void;
  /**
   * Optional search adapter. If absent, the modal renders the "type
   * to search" empty state but does not call any provider.
   */
  readonly onSearch?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<readonly LocationSearchResult[]>;
  /**
   * Optional current-location resolver. The host owns the
   * `navigator.geolocation` call (web) or the platform equivalent
   * (desktop) — this module never calls those APIs directly and
   * never logs the resolved coordinates.
   */
  readonly onResolveCurrentLocation?: (
    signal: AbortSignal,
  ) => Promise<Location | null>;
}

export interface ShareLocationModalProps {
  readonly visible: boolean;
  readonly callbacks: ShareLocationCallbacks;
  /** Default live-share duration in seconds, shown when live is enabled. */
  readonly defaultLiveDurationSeconds?: number;
  /**
   * Conversation display name interpolated into the live-duration sub-modal
   * body ("Everyone in {name} will see your location…"). Falls back to a
   * neutral phrase when omitted.
   */
  readonly conversationName?: string | undefined;
  /**
   * When `true`, the primary action shares a LIVE location: the Share button
   * opens the live-duration sub-modal before emitting `onShare`. When `false`
   * (default) it shares a static point immediately. The host toggles this
   * based on the entry-point (e.g. a "Share live location" menu item).
   */
  readonly liveMode?: boolean | undefined;
}
